import { joinURL } from "ufo";
import type {
  Article,
  BreadcrumbList,
  FAQPage,
  Organization,
  TouristTrip,
  TravelAgency,
  WebPage,
  WebSite,
  WithContext,
} from "schema-dts";
import type { MaybeRefOrGetter } from "vue";
import { computed, toValue } from "vue";
import { SOCIAL_PROFILE_URLS } from "@/utils/socialProfiles";
import { resolveMetaDescription, resolvePageHeading } from "@/utils/pageHeading";

type KirbyPage = Record<string, any> | null | undefined;

type JsonLdDocument =
  | WithContext<
      | WebPage
      | FAQPage
      | Article
      | TouristTrip
      | Organization
      | TravelAgency
      | BreadcrumbList
      | WebSite
    >
  | { "@context": string; "@graph": unknown[] };

interface FaqItem {
  question?: string;
  answer?: string;
}

interface FeefoApiResponse {
  summary?: {
    meta?: {
      count?: number;
      pages?: number;
    };
  };
  meta?: {
    count?: number;
    pages?: number;
  };
  rating?: {
    rating?: number;
    min?: number;
    max?: number;
    service?: {
      count?: number;
    };
  };
  merchant?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  reviews?: unknown[];
}

/** Companies House incorporation date for Mont Blanc Treks Limited (07507824). */
const ORGANIZATION_FOUNDING_DATE = "2011-01-27";

/** Operational base — Chamonix-Mont-Blanc town centre. */
const CHAMONIX_ADDRESS = {
  "@type": "PostalAddress" as const,
  addressLocality: "Chamonix-Mont-Blanc",
  addressRegion: "Haute-Savoie",
  postalCode: "74400",
  addressCountry: "FR",
};

const CHAMONIX_GEO = {
  "@type": "GeoCoordinates" as const,
  latitude: 45.9237,
  longitude: 6.8694,
};

const ORGANIZATION_EMAIL = "info@montblanctreks.com";

export interface TrekPriceItem {
  id?: string;
  trek_price?: string | number;
  trek_route?: string;
  trek_title?: string;
  availability?: string;
  dates?: string;
  datesAvailability?: Array<{
    dates?: string;
    availability?: string;
    spacesAvailable?: string;
    limitedAvailability?: string;
  }>;
}

export interface TrekItineraryDay {
  title?: string;
  text?: string;
  daily_stats?: string;
}

export interface TrekStats {
  distance?: string | null;
  elevation?: string | null;
  difficulty?: string | null;
  duration?: string | null;
  region?: string | null;
}

/** Hub URIs that get flagship TouristTrip + Offer (+ FAQ/rating when available). */
export const FLAGSHIP_TREK_URIS = new Set([
  "tour-du-mont-blanc",
  "walkers-haute-route",
  "tour-of-the-jungfrau",
  "tour-of-the-monte-rosa",
  "the-italian-dolomites",
]);

export function isFlagshipTrekPath(path: string): boolean {
  const segments = path.split("/").filter(Boolean);
  if (segments[0] && FLAGSHIP_TREK_URIS.has(segments[0])) return true;
  // Dedicated TMB commercial landing
  return segments[0] === "tour-du-mont-blanc-self-guided-treks-2026";
}

function absoluteUrl(siteUrl: string, path: string): string {
  return joinURL(siteUrl, path);
}

function organizationId(siteUrl: string): string {
  return `${siteUrl.replace(/\/$/, "")}/#organization`;
}

/** Normalize CMS dates (ISO or UK d/M/Y) to YYYY-MM-DD for schema.org. */
export function toIsoDate(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toISOString().slice(0, 10);
  }
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }

  const uk = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (uk?.[1] && uk[2] && uk[3]) {
    const day = uk[1].padStart(2, "0");
    const month = uk[2].padStart(2, "0");
    const year = uk[3];
    return `${year}-${month}-${day}`;
  }

  const parsed = Date.parse(trimmed);
  if (!Number.isNaN(parsed)) {
    return new Date(parsed).toISOString().slice(0, 10);
  }

  return undefined;
}

function titleCaseSlug(segment: string): string {
  return decodeURIComponent(segment)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function parseTrekPrice(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return undefined;
  const cleaned = value.replace(/[£,\s]/g, "");
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

function stripHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

const SCHEMA_IN_STOCK = "https://schema.org/InStock";
const SCHEMA_SOLD_OUT = "https://schema.org/SoldOut";
const SCHEMA_LIMITED = "https://schema.org/LimitedAvailability";

function schemaAvailability(value: string | null | undefined): string {
  const normalized = String(value ?? "")
    .toLowerCase()
    .trim();
  if (!normalized) return SCHEMA_IN_STOCK;
  if (
    normalized.includes("fully booked") ||
    normalized.includes("sold out") ||
    normalized === "full" ||
    normalized.includes("no availability") ||
    normalized.includes("not available") ||
    normalized.includes("unavailable")
  ) {
    return SCHEMA_SOLD_OUT;
  }
  if (normalized.includes("limited")) {
    return SCHEMA_LIMITED;
  }
  return SCHEMA_IN_STOCK;
}

/** Page hides the price and lists no departures for this exact status. */
function isNoAvailability(value: string | null | undefined): boolean {
  return String(value ?? "").trim().toLowerCase() === "no availability";
}

function isClosedAvailability(value: string | null | undefined): boolean {
  const normalized = String(value ?? "").toLowerCase().trim();
  if (!normalized) return false;
  return (
    normalized.includes("fully booked") ||
    normalized.includes("sold out") ||
    normalized === "full" ||
    normalized.includes("no availability") ||
    normalized.includes("not available")
  );
}

function positiveCount(value: unknown): number | undefined {
  const n = Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Same order as the dates table: a closed row status wins, otherwise a space
 * count is what the page prints ("4 spaces available").
 */
function offerAvailability(
  row:
    | {
        availability?: string;
        spacesAvailable?: string;
        limitedAvailability?: string;
      }
    | undefined,
  parent?: string | null,
): { availability: string; spaces?: number } {
  const own = String(row?.availability ?? "").trim();
  if (own && isClosedAvailability(own)) {
    return { availability: SCHEMA_SOLD_OUT };
  }

  const spaces = positiveCount(row?.spacesAvailable);
  if (spaces) {
    return {
      availability: own.toLowerCase().includes("limited")
        ? SCHEMA_LIMITED
        : SCHEMA_IN_STOCK,
      spaces,
    };
  }

  const limited = positiveCount(row?.limitedAvailability);
  if (limited || own.toLowerCase().includes("limited")) {
    return { availability: SCHEMA_LIMITED, ...(limited ? { spaces: limited } : {}) };
  }

  return { availability: schemaAvailability(own || parent) };
}

const MONTH_INDEX: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

function monthIndex(name: string | undefined): number | undefined {
  if (!name) return undefined;
  return MONTH_INDEX[name.toLowerCase().replace(/\./g, "")];
}

function isoDate(year: number, month: number, day: number): string | undefined {
  if (!year || !month || !day) return undefined;
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (
    dt.getUTCFullYear() !== year ||
    dt.getUTCMonth() !== month - 1 ||
    dt.getUTCDate() !== day
  ) {
    return undefined;
  }
  return dt.toISOString().slice(0, 10);
}

/** Infer season year from page title / CMS year fields (e.g. "… 2026"). */
export function resolveOfferYear(page?: KirbyPage | null): number {
  const haystack = [
    page?.title,
    page?.meta?.metaTitle,
    page?.setYear1,
    page?.setYear2,
    page?.trekDates,
  ]
    .map((v) => String(v ?? ""))
    .join(" ");
  const match = haystack.match(/\b(20\d{2})\b/);
  if (match?.[1]) return Number(match[1]);
  return new Date().getFullYear();
}

/**
 * Parse CMS trek date windows like "15 - 27 June" or "22 June - 4 July".
 */
export function parseTrekDateRange(
  value: string | null | undefined,
  year: number,
): { validFrom?: string; validThrough?: string } {
  const raw = stripHtml(value)
    .replace(/\s+/g, " ")
    .replace(/[–—]/g, "-")
    .trim();
  if (!raw || !year) return {};

  // "22 June - 4 July" / "31 Aug - 12 Sept"
  const cross = raw.match(
    /^(\d{1,2})\s+([A-Za-z]+)\s*-\s*(\d{1,2})\s+([A-Za-z]+)$/,
  );
  if (cross) {
    const fromMonth = monthIndex(cross[2]);
    const toMonth = monthIndex(cross[4]);
    const fromDay = Number(cross[1]);
    const toDay = Number(cross[3]);
    if (!fromMonth || !toMonth) return {};
    let toYear = year;
    if (toMonth < fromMonth || (toMonth === fromMonth && toDay < fromDay)) {
      toYear = year + 1;
    }
    return {
      validFrom: isoDate(year, fromMonth, fromDay),
      validThrough: isoDate(toYear, toMonth, toDay),
    };
  }

  // "15 - 27 June" / "19- 31 July"
  const same = raw.match(/^(\d{1,2})\s*-\s*(\d{1,2})\s+([A-Za-z]+)$/);
  if (same) {
    const month = monthIndex(same[3]);
    const fromDay = Number(same[1]);
    const toDay = Number(same[2]);
    if (!month) return {};
    return {
      validFrom: isoDate(year, month, fromDay),
      validThrough: isoDate(year, month, toDay),
    };
  }

  // Single day "15 June"
  const single = raw.match(/^(\d{1,2})\s+([A-Za-z]+)$/);
  if (single) {
    const month = monthIndex(single[2]);
    const day = Number(single[1]);
    if (!month) return {};
    const date = isoDate(year, month, day);
    return { validFrom: date, validThrough: date };
  }

  return {};
}

function seasonYear(value: unknown, fallback: number): number {
  const match = String(value ?? "").match(/\b(20\d{2})\b/);
  if (match?.[1]) return Number(match[1]);
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 2000 && numeric < 2100) {
    return numeric;
  }
  return fallback;
}

function offerSeasons(
  page: KirbyPage,
  primary?: TrekPriceItem[] | null,
  secondary?: TrekPriceItem[] | null,
): Array<{ year: number; prices: TrekPriceItem[] }> {
  const year1 = seasonYear(page?.setYear1, resolveOfferYear(page));
  const seasons: Array<{ year: number; prices: TrekPriceItem[] }> = [];
  if (Array.isArray(primary) && primary.length > 0) {
    seasons.push({ year: year1, prices: primary });
  }
  if (Array.isArray(secondary) && secondary.length > 0) {
    const year2 = seasonYear(page?.setYear2, year1 + 1);
    seasons.push({
      year: year2 === year1 ? year1 + 1 : year2,
      prices: secondary,
    });
  }
  return seasons;
}

function packageTitleForPrice(
  page: KirbyPage,
  item: TrekPriceItem,
): string | undefined {
  const own = stripHtml(item.trek_title);
  if (own) return own;
  const packages = Array.isArray(page?.trekPackagesList)
    ? page.trekPackagesList
    : [];
  const match = packages.find(
    (pkg: { id?: string; trek_title?: string }) =>
      item.id != null && String(pkg?.id) === String(item.id),
  );
  const title = stripHtml(match?.trek_title);
  return title || undefined;
}

function departureLabel(dates: string | undefined, year: number): string {
  const clean = stripHtml(dates).replace(/\s+/g, " ").trim();
  if (!clean) return year ? String(year) : "";
  if (/\b20\d{2}\b/.test(clean)) return clean;
  return year ? `${clean} ${year}` : clean;
}

function offerName(
  page: KirbyPage,
  item: TrekPriceItem,
  dates: string | undefined,
  year: number,
): string | undefined {
  const label = packageTitleForPrice(page, item) || stripHtml(item.trek_route);
  const when = departureLabel(dates, year);
  const name = [label, when].filter(Boolean).join(": ");
  return name || undefined;
}

type TrekOfferNode = {
  "@type": "Offer";
  name?: string;
  price: string;
  priceCurrency: string;
  url: string;
  availability: string;
  inventoryLevel?: {
    "@type": "QuantitativeValue";
    value: number;
  };
  validFrom?: string;
  validThrough?: string;
  priceValidUntil?: string;
  /** Internal: package key used to group subTrips (stripped before JSON-LD). */
  _packageKey?: string;
  _packageName?: string;
};

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Drop departures that have already ended (or started with no end date in the past). */
function isUpcomingOffer(offer: TrekOfferNode, today = todayIsoDate()): boolean {
  const end = offer.validThrough || offer.validFrom;
  if (!end) return true;
  return end >= today;
}

function packageKeyForPrice(page: KirbyPage | undefined, item: TrekPriceItem): string {
  if (item.id != null && String(item.id).trim()) return `id:${item.id}`;
  const title = packageTitleForPrice(page as KirbyPage, item);
  if (title) return `title:${title.toLowerCase()}`;
  const route = stripHtml(item.trek_route).toLowerCase();
  return route ? `route:${route}` : "package:unknown";
}

function coalesceOffers(
  offerNodes: TrekOfferNode[],
  url: string,
): TouristTrip["offers"] | undefined {
  const upcoming = offerNodes.filter((offer) => isUpcomingOffer(offer));
  if (upcoming.length === 0) return undefined;

  const cleaned = upcoming.map(({ _packageKey, _packageName, ...rest }) => rest);

  if (cleaned.length === 1) {
    return cleaned[0];
  }

  const values = cleaned
    .map((node) => Number(node.price))
    .filter((price) => Number.isFinite(price));
  if (values.length === 0) return undefined;

  return {
    "@type": "AggregateOffer" as const,
    lowPrice: String(Math.min(...values)),
    highPrice: String(Math.max(...values)),
    priceCurrency: "GBP",
    offerCount: cleaned.length,
    url,
    offers: cleaned,
  };
}

function buildTrekOfferNodes(
  seasons: Array<{ year: number; prices: TrekPriceItem[] }>,
  url: string,
  page?: KirbyPage,
): TrekOfferNode[] {
  return seasons.flatMap(({ year, prices }) => {
    const priced = prices
      .map((item) => ({
        item,
        price: parseTrekPrice(item?.trek_price),
      }))
      .filter(
        (entry): entry is { item: TrekPriceItem; price: number } =>
          entry.price != null,
      );

    return priced.flatMap(({ item, price }) => {
      const pkgKey = packageKeyForPrice(page, item);
      const pkgName =
        packageTitleForPrice(page as KirbyPage, item) ||
        stripHtml(item.trek_route) ||
        undefined;
      const dateRows = Array.isArray(item.datesAvailability)
        ? item.datesAvailability.filter((row) => row?.dates)
        : [];

      if (dateRows.length === 0) {
        // The dates table is empty and the card hides this price.
        if (isNoAvailability(item.availability)) return [];
        const stock = offerAvailability(undefined, item.availability);
        return [
          {
            "@type": "Offer" as const,
            name: offerName(page, item, undefined, year),
            price: String(price),
            priceCurrency: "GBP",
            url,
            availability: stock.availability,
            ...(stock.spaces
              ? {
                  inventoryLevel: {
                    "@type": "QuantitativeValue" as const,
                    value: stock.spaces,
                  },
                }
              : {}),
            _packageKey: pkgKey,
            _packageName: pkgName,
          },
        ];
      }

      return dateRows.map((row) => {
        const range = parseTrekDateRange(row.dates, year);
        const stock = offerAvailability(row, item.availability);
        return {
          "@type": "Offer" as const,
          name: offerName(page, item, row.dates, year),
          price: String(price),
          priceCurrency: "GBP",
          url,
          availability: stock.availability,
          ...(stock.spaces
            ? {
                inventoryLevel: {
                  "@type": "QuantitativeValue" as const,
                  value: stock.spaces,
                },
              }
            : {}),
          ...(range.validFrom ? { validFrom: range.validFrom } : {}),
          ...(range.validThrough
            ? {
                validThrough: range.validThrough,
                priceValidUntil: range.validThrough,
              }
            : {}),
          _packageKey: pkgKey,
          _packageName: pkgName,
        };
      });
    });
  });
}

function buildTrekOffers(
  seasons: Array<{ year: number; prices: TrekPriceItem[] }>,
  url: string,
  page?: KirbyPage,
): TouristTrip["offers"] | undefined {
  return coalesceOffers(buildTrekOfferNodes(seasons, url, page), url);
}

/** One TouristTrip per package so prices are not mixed into a single AggregateOffer. */
function buildTrekSubTrips(
  seasons: Array<{ year: number; prices: TrekPriceItem[] }>,
  url: string,
  page?: KirbyPage,
): Array<Record<string, unknown>> {
  const nodes = buildTrekOfferNodes(seasons, url, page).filter((offer) =>
    isUpcomingOffer(offer),
  );
  const byPackage = new Map<string, TrekOfferNode[]>();

  for (const node of nodes) {
    const key = node._packageKey || "package:unknown";
    const list = byPackage.get(key) ?? [];
    list.push(node);
    byPackage.set(key, list);
  }

  const subTrips: Array<Record<string, unknown>> = [];
  for (const [, pkgOffers] of byPackage) {
    const name = pkgOffers.find((o) => o._packageName)?._packageName;
    const offers = coalesceOffers(pkgOffers, url);
    if (!offers) continue;
    subTrips.push({
      "@type": "TouristTrip",
      ...(name ? { name } : {}),
      url,
      offers,
    });
  }

  return subTrips;
}

function buildItineraryItemList(days: TrekItineraryDay[]) {
  const items = days
    .map((day, index) => {
      const name = stripHtml(day.title);
      if (!name) return null;
      const description = stripHtml(
        [day.text, day.daily_stats].filter(Boolean).join(" "),
      );
      return {
        "@type": "ListItem" as const,
        position: index + 1,
        name,
        ...(description ? { description } : {}),
      };
    })
    .filter(Boolean);

  if (items.length === 0) return undefined;

  return {
    "@type": "ItemList" as const,
    numberOfItems: items.length,
    itemListElement: items,
  };
}

function firstText(...values: unknown[]): string | null {
  for (const value of values) {
    const str = stripHtml(value);
    if (str) return str;
  }
  return null;
}

function buildTrekAdditionalProperties(
  stats?: TrekStats | null,
  page?: KirbyPage,
) {
  const entries: Array<{ name: string; value: string }> = [
    { name: "Distance", value: stats?.distance },
    { name: "Height gain", value: stats?.elevation },
    { name: "Grade", value: stats?.difficulty },
    { name: "Region", value: stats?.region },
    { name: "Duration / stages", value: stats?.duration },
  ]
    .map((entry) => {
      const value = stripHtml(entry.value);
      if (!value) return null;
      return { name: entry.name, value };
    })
    .filter((entry): entry is { name: string; value: string } => entry != null);

  const coveredIcons = new Set<string>();
  if (firstText(page?.trek_distance, page?.trekDistance, stats?.distance)) {
    coveredIcons.add("Distance");
  }
  if (firstText(page?.trek_elevation, page?.trekElevation, stats?.elevation)) {
    coveredIcons.add("Ascent Descent");
  }
  const grade = firstText(page?.trek_grade, page?.trekGrade, stats?.difficulty);
  if (grade) {
    const gradeNum = grade.match(/([1-4])/);
    coveredIcons.add(gradeNum ? `Trek Grade ${gradeNum[1]}` : "Trek Grade 3");
  }

  const seen = new Set(entries.map((entry) => entry.value.toLowerCase()));
  const introStats = Array.isArray(page?.trekIntroStats)
    ? page.trekIntroStats
    : [];
  for (const stat of introStats) {
    const value = stripHtml(stat?.statText);
    const icon = String(stat?.statIcon ?? "").trim();
    if (!value) continue;
    if (icon && coveredIcons.has(icon)) continue;
    const lower = value.toLowerCase();
    if (
      [...seen].some(
        (existing) => existing.includes(lower) || lower.includes(existing),
      )
    ) {
      continue;
    }
    entries.push({ name: icon || "Fact", value });
    seen.add(lower);
  }

  if (entries.length === 0) return undefined;

  return entries.map((entry) => ({
    "@type": "PropertyValue" as const,
    name: entry.name,
    value: entry.value,
  }));
}

function matchingSiteChild(page?: KirbyPage | null, site?: KirbyPage | null) {
  const children = Array.isArray(site?.children) ? site.children : [];
  const uri = String(page?.uri || page?.parentPageUri || "")
    .split("/")
    .filter(Boolean)[0];

  return children.find((child: Record<string, unknown>) => {
    const childUri = String(child?.uri || "");
    if (uri && childUri === uri) return true;
    const title = String(child?.title || "").toLowerCase();
    const pageTitle = String(
      page?.title || page?.parentPageTitle || "",
    ).toLowerCase();
    return Boolean(pageTitle && title && pageTitle.includes(title));
  });
}

function introStatText(page: KirbyPage, needle: string): string | null {
  const introStats = Array.isArray(page?.trekIntroStats)
    ? page.trekIntroStats
    : [];
  const stat = introStats.find((item: Record<string, unknown>) =>
    String(item?.statIcon || "")
      .toLowerCase()
      .includes(needle),
  );
  return firstText(stat?.statText);
}

/** Resolve the same distance / grade / region facts the trek page renders. */
export function resolveTrekStats(
  page?: KirbyPage | null,
  site?: KirbyPage | null,
): TrekStats {
  const match = matchingSiteChild(page, site);
  return {
    distance: firstText(
      page?.trek_distance,
      page?.trekDistance,
      match?.trekDistance,
      introStatText(page, "distance"),
    ),
    elevation: firstText(
      page?.trek_elevation,
      page?.trekElevation,
      match?.trekElevation,
      introStatText(page, "ascent"),
      introStatText(page, "elevation"),
    ),
    difficulty: firstText(
      page?.trek_grade,
      page?.trekGrade,
      match?.trekGrade,
    ),
    duration: firstText(
      page?.trek_duration,
      page?.duration,
      page?.trekDuration,
      match?.duration,
      introStatText(page, "duration"),
      introStatText(page, "day"),
    ),
    region: firstText(page?.trek_location, page?.trekLocation),
  };
}

export function flattenFaqItems(
  faqPageItem?: {
    faqs?: FaqItem[] | null;
  } | null,
): FaqItem[] {
  if (!faqPageItem || !Array.isArray(faqPageItem.faqs)) return [];
  // Match AccPanel: question|title and answer|text1 so schema counts match UI
  return faqPageItem.faqs
    .map((faq) => {
      const raw = faq as FaqItem & {
        title?: string;
        text1?: string;
      };
      const question = String(raw?.question || raw?.title || "").trim();
      const answer = String(raw?.answer || raw?.text1 || "").trim();
      return { question, answer };
    })
    .filter((faq) => faq.question && faq.answer);
}

/** Prefer the first non-empty array (avoids `[] ?? fallback` swallowing data). */
export function firstNonEmptyArray<T>(
  ...candidates: Array<T[] | null | undefined>
): T[] {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) return candidate;
  }
  return [];
}

/**
 * Pick the FAQ group that belongs to a trek page from a multi-trek faqPage list.
 */
export function resolveFaqPageItem(
  page?: KirbyPage | null,
  faqPages?: Array<Record<string, unknown>> | null,
): { faqs?: FaqItem[] | null } | null {
  const list = Array.isArray(faqPages)
    ? faqPages
    : Array.isArray(page?.faqPage)
      ? page.faqPage
      : [];
  if (list.length === 0) return null;

  const pageUri = String(page?.uri || page?.parentPageUri || "")
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
  const pageTitle = String(page?.title || page?.parentPageTitle || "")
    .toLowerCase()
    .replace(/['']/g, "");

  const match =
    list.find((item) => {
      const parentUri = String(item?.parentUri || item?.uri || "")
        .replace(/^\/+|\/+$/g, "")
        .toLowerCase();
      const parentTitle = String(item?.parentTitle || "")
        .toLowerCase()
        .replace(/['']/g, "");
      if (pageUri && parentUri.startsWith(pageUri.split("/")[0] || pageUri)) {
        return true;
      }
      if (pageTitle && parentTitle && pageTitle.includes(parentTitle)) {
        return true;
      }
      return false;
    }) ?? null;

  return (match as { faqs?: FaqItem[] | null } | null) || null;
}

export function resolveTrekItineraryDays(
  page?: KirbyPage | null,
  trekPageTrek?: Array<{ trekItinerary?: TrekItineraryDay[] }> | null,
): TrekItineraryDay[] {
  if (Array.isArray(page?.trekItinerary) && page.trekItinerary.length > 0) {
    return page.trekItinerary as TrekItineraryDay[];
  }

  const fromChildren = (trekPageTrek ?? []).find(
    (item) =>
      Array.isArray(item?.trekItinerary) && item.trekItinerary.length > 0,
  );
  return Array.isArray(fromChildren?.trekItinerary)
    ? fromChildren!.trekItinerary!
    : [];
}

function heroImageObject(page: KirbyPage) {
  const image =
    page?.heroImage || page?.cover || page?.coverImage || page?.heroVideoPoster;

  if (!image?.url) return undefined;

  return {
    "@type": "ImageObject" as const,
    url: image.url,
    width: image.width || 800,
    height: image.height || 600,
    caption: image.caption,
  };
}

function videoObject(meta: Record<string, any> | undefined) {
  if (!meta?.sdVideoUrl) return undefined;

  return {
    "@type": "VideoObject" as const,
    name: meta.sdVideoTitle,
    description: meta.sdVideoDescription,
    thumbnailUrl: meta.sdVideoThumbnail?.url,
    uploadDate: meta.sdVideoUploadDate,
    contentUrl: meta.sdVideoUrl,
    embedUrl: meta.sdVideoEmbedUrl,
    duration: meta.sdVideoDuration,
  };
}

function stripContext(node: object): Record<string, unknown> {
  const { "@context": _ctx, ...rest } = node as Record<string, unknown>;
  return rest;
}

export function useJsonLdHead(
  jsonLd: MaybeRefOrGetter<JsonLdDocument | JsonLdDocument[] | null>,
) {
  useHead({
    script: computed(() => {
      const data = toValue(jsonLd);
      if (!data) return [];

      const items = Array.isArray(data) ? data : [data];

      return items.map((item, i) => ({
        key: `json-ld-${i}`,
        type: "application/ld+json",
        innerHTML: JSON.stringify(item),
      }));
    }),
  });
}

/** Pair a primary schema node with BreadcrumbList for the path. */
export function withBreadcrumbJsonLd(
  primary: JsonLdDocument,
  path: string,
  siteUrl: string,
  labels?: string[],
): JsonLdDocument[] {
  return [primary, buildBreadcrumbListJsonLd(path, siteUrl, labels)];
}

export function buildBreadcrumbListJsonLd(
  path: string,
  siteUrl: string,
  labels?: string[],
): WithContext<BreadcrumbList> {
  const segments = path.split("/").filter(Boolean);
  const itemListElement: Array<{
    "@type": "ListItem";
    position: number;
    name: string;
    item: string;
  }> = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: siteUrl.replace(/\/$/, "") || siteUrl,
    },
  ];

  let cumulative = "";
  segments.forEach((segment, index) => {
    cumulative += `/${segment}`;
    itemListElement.push({
      "@type": "ListItem",
      position: index + 2,
      name: labels?.[index] ?? titleCaseSlug(segment),
      item: absoluteUrl(siteUrl, cumulative),
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
}

export function buildWebPageJsonLd(
  page: KirbyPage,
  path: string,
  siteUrl: string,
): WithContext<WebPage> {
  const meta = page?.meta;
  const url = absoluteUrl(siteUrl, path);

  const jsonLd: WithContext<WebPage> = {
    "@context": "https://schema.org",
    "@type": (meta?.structuredDataType as WebPage["@type"]) || "WebPage",
    name: meta?.metaTitle || resolvePageHeading(page),
    headline: resolvePageHeading(page),
    description: resolveMetaDescription(page),
    url,
  };

  const image = heroImageObject(page);
  if (image) jsonLd.image = image;

  const video = videoObject(meta);
  if (video) jsonLd.video = video;

  return jsonLd;
}

export function buildArticleJsonLd(
  page: KirbyPage,
  path: string,
  siteUrl: string,
  site?: KirbyPage,
): WithContext<Article> {
  const url = absoluteUrl(siteUrl, path);
  const datePublished =
    toIsoDate(page?.published) ?? toIsoDate(page?.date) ?? undefined;
  const dateModified =
    toIsoDate(page?.modified) ??
    toIsoDate(page?.updated) ??
    datePublished;

  const description =
    resolveMetaDescription(page, site) ||
    stripHtml(page?.intro || page?.excerpt || page?.text || "");

  const jsonLd: WithContext<Article> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page?.title,
    description: description || undefined,
    url,
    publisher: {
      "@type": "Organization",
      name: site?.title || "Mont Blanc Treks",
      url: siteUrl,
      ...(site?.logo?.url || site?.feefo?.url
        ? { logo: site?.logo?.url || site?.feefo?.url }
        : {}),
    },
  };

  if (datePublished) jsonLd.datePublished = datePublished;
  if (dateModified) jsonLd.dateModified = dateModified;

  if (page?.blogImage?.url) {
    jsonLd.image = page.blogImage.url;
  }

  const authorName = stripHtml(page?.author || page?.author2 || "");
  if (authorName) {
    jsonLd.author = {
      "@type": "Person",
      name: authorName,
    };
  } else {
    jsonLd.author = {
      "@type": "Organization",
      name: site?.title || "Mont Blanc Treks",
      url: siteUrl,
    };
  }

  return jsonLd;
}

export function buildFaqPageJsonLd(
  page: KirbyPage,
  path: string,
  siteUrl: string,
  faqs: FaqItem[],
): WithContext<FAQPage> {
  const meta = page?.meta;
  const url = absoluteUrl(siteUrl, path);

  const mainEntity = faqs
    .filter((faq) => faq?.question && faq?.answer)
    .map((faq) => ({
      "@type": "Question" as const,
      name: stripHtml(faq.question),
      acceptedAnswer: {
        "@type": "Answer" as const,
        text: stripHtml(faq.answer),
      },
    }))
    .filter((faq) => faq.name && faq.acceptedAnswer.text);

  const jsonLd: WithContext<FAQPage> = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    name: meta?.metaTitle || resolvePageHeading(page),
    headline: resolvePageHeading(page),
    description: resolveMetaDescription(page),
    url,
    mainEntity: mainEntity.length > 0 ? mainEntity : undefined,
  };

  const image = heroImageObject(page);
  if (image) jsonLd.image = image;

  return jsonLd;
}

export function buildTouristTripJsonLd(
  pageMeta: KirbyPage,
  path: string,
  siteUrl: string,
  options?: {
    trekprices?: TrekPriceItem[] | null;
    trekprices2?: TrekPriceItem[] | null;
    site?: KirbyPage;
    itinerary?: TrekItineraryDay[] | null;
    stats?: TrekStats | null;
    feefoData?: FeefoApiResponse | null;
  },
): WithContext<TouristTrip> {
  const meta = pageMeta?.meta as Record<string, any> | undefined;
  const url = absoluteUrl(siteUrl, path);
  const site = options?.site;
  const stats = options?.stats ?? resolveTrekStats(pageMeta, site);
  const aggregateRating = extractFeefoAggregateRating(options?.feefoData);

  const provider: Record<string, unknown> = {
    "@type": "TravelAgency",
    "@id": organizationId(siteUrl),
    name: site?.title || "Mont Blanc Treks",
    url: siteUrl,
  };
  // Feefo score is merchant-wide — attach to the agency, not the trip entity.
  if (aggregateRating) {
    provider.aggregateRating = aggregateRating;
  }

  const jsonLd: WithContext<TouristTrip> & {
    additionalProperty?: unknown;
    subTrip?: unknown;
  } = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: meta?.metaTitle || pageMeta?.title,
    description: meta?.description || resolveMetaDescription(pageMeta, site),
    url,
    touristType: "Trekking holiday",
    provider: provider as TouristTrip["provider"],
  };

  const itineraryDays =
    options?.itinerary ?? resolveTrekItineraryDays(pageMeta);
  const itineraryList = buildItineraryItemList(itineraryDays);
  if (itineraryList) {
    jsonLd.itinerary = itineraryList as TouristTrip["itinerary"];
  }

  const additionalProperty = buildTrekAdditionalProperties(stats, pageMeta);
  if (additionalProperty) {
    jsonLd.additionalProperty = additionalProperty;
  }

  const image = heroImageObject(pageMeta);
  if (image) jsonLd.image = image;

  const seasons = offerSeasons(
    pageMeta,
    options?.trekprices,
    options?.trekprices2,
  );
  const subTrips = buildTrekSubTrips(seasons, url, pageMeta);
  if (subTrips.length > 1) {
    // Multiple packages → one subTrip each (avoids mixing package prices).
    jsonLd.subTrip = subTrips;
  } else if (subTrips.length === 1) {
    // Single package: keep offers on the parent trip.
    const only = subTrips[0]?.offers;
    if (only) jsonLd.offers = only as TouristTrip["offers"];
  } else {
    const offers = buildTrekOffers(seasons, url, pageMeta);
    if (offers) jsonLd.offers = offers;
  }

  return jsonLd;
}

/**
 * Flagship trek graph: TouristTrip (+ Offer/AggregateRating) and FAQPage when FAQs exist.
 */
export function buildFlagshipTrekGraphJsonLd(
  page: KirbyPage,
  path: string,
  siteUrl: string,
  options?: {
    trekprices?: TrekPriceItem[] | null;
    trekprices2?: TrekPriceItem[] | null;
    site?: KirbyPage;
    itinerary?: TrekItineraryDay[] | null;
    stats?: TrekStats | null;
    faqs?: FaqItem[] | null;
    feefoData?: FeefoApiResponse | null;
  },
) {
  const graph: unknown[] = [
    stripContext(
      buildTouristTripJsonLd(page, path, siteUrl, {
        trekprices: options?.trekprices,
        trekprices2: options?.trekprices2,
        site: options?.site,
        itinerary: options?.itinerary,
        stats: options?.stats,
        feefoData: options?.feefoData,
      }),
    ),
  ];

  const faqs = (options?.faqs ?? []).filter(
    (faq) => faq?.question && faq?.answer,
  );
  if (faqs.length > 0) {
    graph.push(
      stripContext(buildFaqPageJsonLd(page, path, siteUrl, faqs)),
    );
  }

  graph.push(stripContext(buildBreadcrumbListJsonLd(path, siteUrl)));

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export function buildOrganizationJsonLd(
  site: KirbyPage,
  siteUrl: string,
  feefoData?: FeefoApiResponse | null,
): WithContext<TravelAgency> {
  const logo = site?.logo?.url || site?.feefo?.url;
  const email = site?.emailContact || ORGANIZATION_EMAIL;
  const phone = String(site?.phone ?? "").trim();
  const contactUrl = absoluteUrl(siteUrl, "/get-in-touch");

  const jsonLd: WithContext<TravelAgency> = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "@id": organizationId(siteUrl),
    name: site?.title || "Mont Blanc Treks",
    url: siteUrl,
    email,
    description: site?.description,
    foundingDate: ORGANIZATION_FOUNDING_DATE,
    address: CHAMONIX_ADDRESS,
    geo: CHAMONIX_GEO,
    sameAs: [...SOCIAL_PROFILE_URLS],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email,
      url: contactUrl,
      availableLanguage: ["English", "French"],
      ...(phone ? { telephone: phone } : {}),
    },
  };

  if (phone) {
    jsonLd.telephone = phone;
  }

  if (logo) {
    jsonLd.logo = logo;
  }

  const aggregateRating = extractFeefoAggregateRating(feefoData);
  if (aggregateRating) {
    jsonLd.aggregateRating = aggregateRating;
  }

  return jsonLd;
}

export function buildWebSiteJsonLd(
  site: KirbyPage,
  siteUrl: string,
): WithContext<WebSite> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: site?.title || "Mont Blanc Treks",
    url: siteUrl,
    publisher: {
      "@id": organizationId(siteUrl),
    },
  };
}

export function buildHomePageGraphJsonLd(
  page: KirbyPage,
  site: KirbyPage,
  path: string,
  siteUrl: string,
  feefoData?: FeefoApiResponse | null,
) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      stripContext(buildOrganizationJsonLd(site, siteUrl, feefoData)),
      stripContext(buildWebSiteJsonLd(site, siteUrl)),
      stripContext(buildWebPageJsonLd(page, path, siteUrl)),
    ],
  };
}

export function extractFeefoAggregateRating(
  feefoData: FeefoApiResponse | null | undefined,
) {
  if (!feefoData) return undefined;

  // Only emit when Feefo summary score is present — never average a
  // partial reviews page against the total review count.
  const summaryRating = feefoData.rating?.rating;
  const reviewCount =
    feefoData.rating?.service?.count ??
    feefoData.summary?.meta?.count ??
    feefoData.meta?.count;

  if (
    typeof summaryRating !== "number" ||
    !Number.isFinite(summaryRating) ||
    !reviewCount
  ) {
    return undefined;
  }

  return {
    "@type": "AggregateRating" as const,
    ratingValue: Math.round(summaryRating * 10) / 10,
    reviewCount,
    bestRating: feefoData.rating?.max ?? 5,
    worstRating: feefoData.rating?.min ?? 1,
  };
}

export function buildReviewsJsonLd(
  page: KirbyPage,
  path: string,
  siteUrl: string,
  _feefoData?: FeefoApiResponse | null,
): WithContext<WebPage> {
  // AggregateRating belongs on Organization (see reviews page graph), not WebPage.
  const jsonLd = buildWebPageJsonLd(page, path, siteUrl);

  // Outdated CMS copy often still says "5 years" — prefer a fresh fallback.
  const rawDescription = String(jsonLd.description ?? "");
  if (/\b5 years\b/i.test(rawDescription)) {
    jsonLd.description =
      "Independent Feefo customer reviews for Mont Blanc Treks guided and self-guided trekking holidays.";
  }

  return jsonLd;
}
